import { yaml as yamlLang } from '@codemirror/lang-yaml';
import { lintGutter } from '@codemirror/lint';
import {
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderFieldError,
} from '@internal/plugin-scaffolder-common-field-react';
import { githubLight } from '@uiw/codemirror-theme-github';
import CodeMirror from '@uiw/react-codemirror';
import React, { useCallback, useEffect, useRef } from 'react';

import { Box } from '@material-ui/core';
import ErrorOutline from '@material-ui/icons/ErrorOutline';
import ReportProblemOutlined from '@material-ui/icons/ReportProblemOutlined';

import { useYamlLinter } from '../hooks/useYamlLinter';
import { GENERATORS_INFO } from '../infoDoc';
import { argoCDGeneratorSchema } from '../schema/generatorsSchema';
import { useStyles, useGeneratorsStyles } from '../styles';
import { generatorsInputErrors } from '../validation';

interface ApplicationSetGeneratorsSectionProps {
  value: string;
  onChange: (value: string) => void;
  errors: string[];
}

const ApplicationSetGeneratorsSectionInner = ({
  value,
  onChange,
  errors,
}: ApplicationSetGeneratorsSectionProps) => {
  const classes = useStyles();
  const generatorClasses = useGeneratorsStyles();
  const sectionRef = useRef<HTMLDivElement>(null);
  const generatorsError = errors.length > 0 ? generatorsInputErrors(value) : '';
  const {
    yamlLinter,
    errors: yamlErrors,
    warnings: yamlWarnings,
  } = useYamlLinter(argoCDGeneratorSchema);

  // Stable refs — keeps useCallback deps empty ([]) without stale closures.
  // Always read .current inside callbacks instead of closing over the prop directly.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (generatorsError) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [generatorsError]);

  const handleChange = useCallback((v: string) => {
    onChangeRef.current(v);
  }, []);

  return (
    <div ref={sectionRef}>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Generators"
          description="Define generators to automatically create multiple ArgoCD Applications from this ApplicationSet."
          divider
          infoDoc={GENERATORS_INFO}
        />
        <Box margin={1}>
          <Box className={generatorClasses.codeMirror}>
            <CodeMirror
              value={value || ''}
              minHeight="300px"
              extensions={[
                typeof yamlLang === 'function' ? yamlLang() : ([] as any),
                lintGutter(),
                yamlLinter,
              ]}
              onChange={handleChange}
              theme={githubLight}
              placeholder="Enter ArgoCD ApplicationSet generator YAML"
            />
          </Box>
          <Box className={generatorClasses.validationContainer}>
            <Box
              display="inline-flex"
              alignItems="center"
              aria-label="error-count"
              title={`${yamlErrors?.length ?? 0} error${
                (yamlErrors?.length ?? 0) === 1 ? '' : 's'
              }`}
              className={
                (yamlErrors?.length ?? 0) > 0
                  ? generatorClasses.validationErrorStyle
                  : generatorClasses.validationDefaultStyle
              }
            >
              <ErrorOutline fontSize="small" style={{ marginRight: 6 }} />
              <span>{yamlErrors?.length ?? 0}</span>
            </Box>
            <Box
              display="inline-flex"
              alignItems="center"
              aria-label="warning-count"
              title={`${yamlWarnings?.length ?? 0} warning${
                (yamlWarnings?.length ?? 0) === 1 ? '' : 's'
              }`}
              className={
                (yamlWarnings?.length ?? 0) > 0
                  ? generatorClasses.validationWarningStyle
                  : generatorClasses.validationDefaultStyle
              }
            >
              <ReportProblemOutlined
                fontSize="small"
                style={{ marginRight: 6 }}
              />
              <span>{yamlWarnings?.length ?? 0}</span>
            </Box>
          </Box>
          {generatorsError && (
            <Box className={classes.inputContainer}>
              <ScaffolderFieldError message={generatorsError} />
            </Box>
          )}
        </Box>
      </ScaffolderFormPaper>
    </div>
  );
};

export const ApplicationSetGeneratorsSection = React.memo(
  ApplicationSetGeneratorsSectionInner,
);
